// Dominio de Partnerships B2B2C (Laundryheap LATAM)

export type PartnershipType = 'strategic_global' | 'local_operational';

// Etapas del pipeline de una alianza
export type PartnerStatus =
  | 'not_started'
  | 'researching'
  | 'intro_sent'
  | 'meeting_set'
  | 'negotiating'
  | 'signed'
  | 'stalled'
  | 'passed';

export type Priority = 'high' | 'medium' | 'low';

// Decision maker (estructura espejo de lib/hunter.ts)
export interface DecisionMaker {
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  position: string | null;
  seniority: string | null;
  department: string | null;
  confidence: number;
  linkedin: string | null;
  phone: string | null;
}

export interface Partner {
  id: string;
  partner: string;
  category: string;
  partnership_type: PartnershipType;
  region: string | null;
  country: string | null;
  status: PartnerStatus;
  priority: Priority | null;
  probability: number | null;
  website: string | null;
  intro_path: string | null;
  next_action: string | null;
  notes: string | null;
  decision_makers: DecisionMaker[];
  last_contact_at: string | null;
  created_at: string;
  updated_at: string;
}

export type ActivityType = 'note' | 'email' | 'meeting' | 'call' | 'intro' | 'stage_change';

export interface PartnerActivity {
  id: string;
  partner_id: string;
  user_id: string | null;
  action_type: ActivityType;
  notes: string | null;
  created_at: string;
}

// ============================================================
// Catálogos (en TS, no en DB — más simple de extender)
// ============================================================

export interface CategoryDef {
  id: string;
  label: string;
  emoji: string;
}

// 5 categorías vistas en el Excel de Deyan + 5 de alto fit LATAM.
// Crecen orgánicamente: agregar aquí cuando aparezca una nueva.
export const CATEGORIES: CategoryDef[] = [
  { id: 'airline_loyalty', label: 'Airline Loyalty', emoji: '✈️' },
  { id: 'supermarket_loyalty', label: 'Supermarket Loyalty', emoji: '🛒' },
  { id: 'deals_voucher', label: 'Deals & Voucher', emoji: '🏷️' },
  { id: 'services_marketplace', label: 'Services Marketplace', emoji: '🛠️' },
  { id: 'grocery_meal', label: 'Grocery / Meal', emoji: '🥗' },
  { id: 'super_app', label: 'Super-App', emoji: '📱' },
  { id: 'hotel_loyalty', label: 'Hotel Loyalty', emoji: '🏨' },
  { id: 'fintech_wallet', label: 'Fintech / Wallet', emoji: '💳' },
  { id: 'coliving_proptech', label: 'Coliving / PropTech', emoji: '🏢' },
  { id: 'corporate_benefits', label: 'Corporate Benefits', emoji: '🎁' },
  { id: 'salud', label: 'Salud (clínicas + colegios)', emoji: '🏥' },
  { id: 'benefit_platform', label: 'Plataforma de Beneficios', emoji: '🎫' },
  { id: 'banca_loyalty', label: 'Banca / Loyalty', emoji: '🏦' },
  { id: 'hospitality_shortstay', label: 'Hospedaje / Short-stay', emoji: '🛏️' },
  { id: 'relocation', label: 'Relocation', emoji: '📦' },
  { id: 'universities', label: 'Universidades', emoji: '🎓' },
  { id: 'new_parents', label: 'Nuevos Padres', emoji: '👶' },
  { id: 'proptech_handover', label: 'Entrega de Deptos', emoji: '🔑' },
  { id: 'retail_beauty', label: 'Retail Belleza', emoji: '💄' },
  { id: 'retail_home', label: 'Retail Hogar', emoji: '🛋️' },
  { id: 'marketing_partnership', label: 'Marketing Partnership', emoji: '🤝' },
  { id: 'por_verificar', label: 'Por Verificar', emoji: '🔍' },
];

export const CATEGORY_LABELS: Record<string, CategoryDef> = Object.fromEntries(
  CATEGORIES.map((c) => [c.id, c])
);

export interface StatusDef {
  id: PartnerStatus;
  label: string;
  color: string;
}

// Pipeline del Kanban (orden = columnas de izquierda a derecha)
export const STATUSES: StatusDef[] = [
  { id: 'not_started', label: 'Not Started', color: '#94A3B8' },
  { id: 'researching', label: 'Researching', color: '#0890F1' },
  { id: 'intro_sent', label: 'Intro Sent', color: '#6366F1' },
  { id: 'meeting_set', label: 'Meeting Set', color: '#A855F7' },
  { id: 'negotiating', label: 'Negotiating', color: '#F59E0B' },
  { id: 'signed', label: 'Signed', color: '#22C55E' },
  { id: 'stalled', label: 'Stalled', color: '#EF4444' },
  { id: 'passed', label: 'Passed', color: '#64748B' },
];

export const STATUS_LABELS: Record<PartnerStatus, StatusDef> = Object.fromEntries(
  STATUSES.map((s) => [s.id, s])
) as Record<PartnerStatus, StatusDef>;

// Países objetivo, en orden de prioridad de lanzamiento
export const COUNTRIES = ['México', 'Perú', 'Colombia'] as const;
export type Country = (typeof COUNTRIES)[number];
